import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { AppError } from '@vk-hr-tek/core/error';
import { showCustomNotification } from '@vk-hr-tek/core/notifications';

import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import {
  DashboardService,
  GetAccountWidgetBirthdaysResponse,
  UpdateWidgetBirthdaysRequest,
} from '@app/gen/dashboard';
import { ThunkExtra, ThunkValuesAndActions } from '@app/store';
import { Entity } from '@app/types';

import { SettingsWithRootState } from '../../../../slice';
import { SettingsState } from '../../../settings.state';

export const getBirthdaysSettings = createAsyncThunk<
  GetAccountWidgetBirthdaysResponse,
  { widgetId: string; accountId: string; companyId?: string },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getBirthdaysSettings',
  async (
    { widgetId, accountId, companyId },
    { rejectWithValue, extra: { inject } },
  ) => {
    try {
      const service = inject(DashboardService);

      const result = await service.getAccountWidgetBirthdays({
        params: {
          widget_id: widgetId,
          account_id: accountId,
        },
        ...(companyId && {
          query: {
            company_id: companyId,
          },
        }),
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateBirthdaysSettings = createAsyncThunk<
  void,
  ThunkValuesAndActions<{
    widgetId: string;
    accountId: string;
    companyId?: string;
    body: UpdateWidgetBirthdaysRequest;
  }>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateBirthdaysSettings',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(DashboardService);

      const { widgetId, accountId, companyId, body } = values;

      await service.updateWidgetBirthdays({
        params: {
          widget_id: widgetId,
          account_id: accountId,
        },
        ...(companyId && {
          query: {
            company_id: companyId,
          },
        }),
        body,
      });

      dispatch(
        showCustomNotification([
          {
            title: 'Виджет успешно изменен',
            message: 'Дни рождения',
            type: 'info',
          },
        ]),
      );
      actions.resolve(null);
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      dispatch(
        showCustomNotification([
          {
            title: 'Не удалось сохранить виджет',
            message: 'Попробуйте еще раз',
            type: 'error',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsBirthdaysReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getBirthdaysSettings.pending, (state) => {
    state.birthdays.status = 'loading';
    state.birthdays.error = null;
  });
  builder.addCase(getBirthdaysSettings.fulfilled, (state, { payload }) => {
    state.birthdays.status = 'complete';
    state.birthdays.isException = !!payload?.company_content_id;
    state.birthdays.data = payload;
  });
  builder.addCase(
    getBirthdaysSettings.rejected,
    (state, { payload, error }) => {
      state.birthdays.status = 'failed';
      state.birthdays.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );
};
